import Api from "@/modules/axios";
import i18n from "@/modules/i18n";
import { useToast } from "@/utils/toast";
import { setHideModalImportPanier } from "@/modules/utils";
import Panier from "@/domains/paniers/panier";

function extractErrorMsg(err) {
  const data = err.response?.data;
  return (typeof data === "object" && data?.detail) || data || i18n.global.t("info.une-erreur-est-survenue");
}

export default {
  state: {
    panier: null,
    isFetchingPanier: false,
  },
  getters: {
    panier(state) {
      return state.panier;
    },
    isFetchingPanier(state) {
      return state.isFetchingPanier;
    },
  },
  mutations: {
    setPanier(state, panier) {
      state.panier = new Panier(panier);
      state.isFetchingPanier = false;
    },
    setIsFetchingPanier(state, bool) {
      state.isFetchingPanier = bool;
    },
  },
  actions: {
    /**
     * Met en place ou met à jour le panier pour l'utilisateur.
     * @param {Object} commit
     * @param {Object} getters
     * @returns {Promise} Panier de l'utilisateur.
     */
    async setPanier({ commit, getters, dispatch }) {
      if (!getters.isLogged) {
        dispatch("setPanierSession");
        return null;
      }
      commit("setIsFetchingPanier", true);
      const params = {
        utilisateur: getters.user.id,
        organisme: getters.organismeActuel.id,
      };

      return Api().get("/liste/panier/", { params })
        .then(({ data }) => {
          commit("setPanier", data);
          return getters.panier;
        })
        .catch((err) => {
          useToast().error({ title: extractErrorMsg(err) });
        });
    },
    /**
     * Met en place ou met à jour le panier de session pour les utilisateurs non connectés.
     * @param {Object} commit
     * @param {Object} getters
     * @returns {Promise} Panier de session.
     */
    async setPanierSession({ commit, getters, rootGetters }) {
      commit("setIsFetchingPanier", true);
      return Api().get("/session_panier/")
        .then(({ data }) => {
          commit("setPanier", data);
          return getters.panier;
        })
        .catch((err) => {
          const status = err.response?.status;
          // Si le backend renvoie une erreur de session (403/500) et qu'on n'est pas connecté côté front,
          // c'est que la session a changé depuis un autre onglet
          if (!rootGetters.isLogged && (status === 403 || status === 500)) {
            commit("setSessionConflict", true, { root: true });
          } else {
            useToast().error({ title: extractErrorMsg(err) });
          }
        })
        .finally(() => {
          commit("setIsFetchingPanier", false);
        });
    },
    /**
     * Met les offres dans le panier de session.
     * @param {Object} commit
     * @param {Object} dispatch
     * @param {Array} lignes Offres à mettre dans le panier.
     */
    addOffersToPanierSession({ commit, dispatch, rootGetters }, { lignes = [] }) {
      commit("setIsFetchingPanier", true);
      return Api().post("/session_panier/add_lignes/", { lignes })
        .then(({ data }) => {
          const msg = data.already_in_list
            ? i18n.global.t("panier.offre-ajoutee-deja-dans-panier")
            : i18n.global.t("panier.offre-bien-ajoutee-panier", lignes.length);
          useToast().success({ title: msg });
          setHideModalImportPanier(false);
        })
        .catch((err) => {
          const status = err.response?.status;
          if (!rootGetters.isLogged && (status === 403 || status === 500)) {
            commit("setSessionConflict", true, { root: true });
          } else {
            useToast().error({ title: extractErrorMsg(err) });
          }
        })
        .finally(() => {
          dispatch("setPanier");
        });
    },
    /**
     * Ajoute les lignes de commande au panier.
     * @param {Object} commit
     * @param {Object} getters
     * @param {Object} dispatch
     * @param {Array} lignes Lignes de commande/Offres à mettre dans le panier.
     * @returns {Promise}
     */
    addOrderLinesToPanier({ commit, getters, dispatch }, commandes = []) {
      commit("setIsFetchingPanier", true);
      const panierId = getters.panier.id;

      return Api().post(`/liste/${panierId}/copy_order/`, { commandes, panier: true })
        .then(({ data }) => {
          const toast = useToast();
          if (data.code === "Aucune") {
            toast.error({ title: i18n.global.t("panier.tous-les-produits-deja-dans-panier") });
          } else {
            if (data.code === "Min une") {
              const msg = i18n.global.t("panier.toutes-offres-pas-mesure-ajoutees-panier");
              toast.warning({ title: msg });
            }
            toast.success({
              title: i18n.global.t("panier.reference-bien-ajoutee-panier", 2),
            });
            setHideModalImportPanier(false);
          }
        })
        .catch((err) => {
          useToast().error({ title: extractErrorMsg(err) });
        })
        .finally(() => {
          dispatch("setPanier");
        });
    },
  },
};
